import { getProductDetailMainElement } from "../product-detail";
import { devLog, displayError, localize } from "../common";
import { IWebsiteIntegration, EditorArguments, DesignerEntityEvent, IPrintJobDto, ArgAddPrinting, ArgCustomer } from "@promotron/website-integration-types";
import HttpClient from "../http-client";
import LoadingIndication from "../loading-indication";
import { openTronDesignerStartDialog } from "./start-dialog";
import { getIndependentPrintJobGuid, setIndependentPrintJobGuid, clearIndependentPrintJob } from "./independent-print-jobs";
import { getTlLinkLogoGuid, isTlLogoImportable } from "./tl-logo-handover";
import { logProductEvent, ProductEventType } from "../product-event";

declare global {
    interface Window {
        tronDesigner: IWebsiteIntegration;
        // Wired as data-td-script-init-failed-callback on the SDK <script>; the SDK calls it when init fails
        // (e.g. a bad api key). Defined at module load so it exists before the async SDK resolves/fails.
        tronDesignerScriptInitFailed?: (error?: unknown) => void;
    }
}

interface TronDesignerProductData {
    supplierGuid: string;
    modelCode: string;
    productCode: string;
    // §4.1 — the product title and the shop-facing product code as shown on the product page. Passed to the
    // editor as the print job title and the variant code/name overrides so the proofreading matches the page.
    productId: string;
    title: string;
    shopProductCode: string;
}

const TD_READY_POLL_INTERVAL_MS = 250;
const TD_READY_MAX_ATTEMPTS = 40;
// Once window.tronDesigner exists we await getInitializing(); cap that wait so a broken/hung SDK
// (bad license, runtime error) can't leave it pending forever and hang every awaiting caller
// (e.g. the cart logo-gallery spinner). Generous vs real init times, but finite.
const TD_INIT_TIMEOUT_MS = 15000;
// If the editor reports a Confirmed close but no printJob created/updated event arrives within this window,
// we recover by reloading so the cart never sticks in an unlinked state (see openDesignerDialog).
const TD_SAVE_FALLBACK_MS = 12000;

let _globalEventsBound = false;
// Bumped on every printJob created/updated event with a guid. Used as a "did a save actually happen?" marker
// for the Confirmed-close fallback below.
let _saveSeq = 0;

// The cart print-set item id the editor was last opened against, captured from the open-time args (which we
// control) in openDesignerDialog. On EDIT of an EXISTING print job, TD echoes the customer/customProperties
// the job was first SAVED with — a stale cart-item id from an earlier cart state — so deriving the re-link
// target from the event alone points at a cart item that no longer exists (POST .../printjob 404s). The id
// captured here is always the live print set. null when opened without a cart print set (product-detail
// design-first), so that path still falls through to the sessionStorage "pending free design" handling.
let _activeCartPrintSetItemId: string | null = null;

// Set when the editor was opened for an INDEPENDENT print job (Addendum 2 §1.2 option 1 — proofreading
// only, never connected to a cart item). Save events then record the job in the independent session store
// (when the product store key is known) instead of stashing a "pending free design" cart link. The mode is
// a flag of its own so a missing store key can never silently degrade into the cart-linking behaviour.
let _activeIndependentMode = false;
let _activeIndependentStoreKey: string | null = null;

// The config the editor saved the design with, sent to the server on link so a later TCC change can be
// detected as a §4.4 mismatch (one entry per print position). Mirrors C# PrintJobMotiveConfig (camelCase).
interface DesignMotiveConfig {
    printPositionCode: string;
    printTechnologyCode: string;
    colors?: number;
    area?: number; // mm² (design's real area = widthMm × heightMm)
    // The design's real dimensions (mm) — displayed in the cart as "Actual size"; area stays the value
    // the server compares against the priced tier.
    widthMm?: number;
    heightMm?: number;
}

// Read the design's actual per-motive config from the editor's printJob event.
// UNITS: widthMm/heightMm are millimetres — TD's integration API has no centimetre unit (its MeasurementUnit
// enum is only PIXELS | MILLIMETERS) and the field is explicitly "...Mm". So design area (w×h) is in mm²,
// matching the TCC tier the server compares against (AREA is cm², converted ×100 to mm² server-side).
// If real-world data ever proves otherwise, this is the single place to convert (multiply w*h by 100 for cm).
function extractDesignConfig(event: DesignerEntityEvent<IPrintJobDto>): DesignMotiveConfig[] {
    return (event.detail?.printings ?? []).map(p => {
        const w = p.printMotive?.widthMm;
        const h = p.printMotive?.heightMm;
        return {
            printPositionCode: p.printData?.positionCodePtn ?? "",
            printTechnologyCode: p.printData?.technologyCodePtn ?? "",
            colors: p.printMotive?.colorList?.length,
            area: (typeof w === "number" && typeof h === "number") ? w * h : undefined, // mm²
            widthMm: typeof w === "number" ? w : undefined,
            heightMm: typeof h === "number" ? h : undefined,
        };
    });
}

// One product variant of the saved design, snapshot for the server's variant mismatch check
// (mirrors C# PrintJobVariantConfig, camelCase). productCode is the supplier variant code — the
// namespace the editor works with and the cart's SupplierCode compares against.
interface DesignVariantConfig {
    productCode: string;
    quantity?: number;
}

// The design's product variants from the editor's printJob event — a later TCC recalculation that
// adds/removes a variant (or changes a quantity) is then detected as a §4.4 mismatch server-side.
function extractDesignVariants(event: DesignerEntityEvent<IPrintJobDto>): DesignVariantConfig[] {
    return (event.detail?.product?.variants ?? [])
        .map(v => ({
            productCode: v.codeSupplier || v.codePtn,
            quantity: typeof v.quantity === "number" ? v.quantity : undefined,
        }))
        .filter(v => !!v.productCode);
}

// Design-First (Scenario 2): the editor saves a Print Job BEFORE any cart item exists, and the customer
// then crosses through TCC (a navigation) before the cart item is created. Persist the guid + design config
// in sessionStorage so they survive that round-trip; linked one-shot on the cart page
// (linkPendingFreeDesignToCart).
const PENDING_FREE_PJ_KEY = "td_pending_free_print_job";

interface PendingFreeDesign {
    printJobGuid: string;
    designConfig: DesignMotiveConfig[];
    designVariants?: DesignVariantConfig[];
}

function setPendingFreeDesign(value: PendingFreeDesign): void {
    try { window.sessionStorage.setItem(PENDING_FREE_PJ_KEY, JSON.stringify(value)); }
    catch { /* sessionStorage unavailable (e.g. private mode) — free-design linking is best-effort */ }
}

function getPendingFreeDesign(): PendingFreeDesign | null {
    try {
        const raw = window.sessionStorage.getItem(PENDING_FREE_PJ_KEY);
        return raw ? JSON.parse(raw) as PendingFreeDesign : null;
    } catch { return null; }
}

// Once the SDK has reported init failure (expired/inactive license, bad key — the
// data-td-script-init-failed-callback fired), every getTronDesignerAsync caller must fail FAST: without
// this, cart design rows and other waiters sat in skeletons for the full 15s init timeout after the SDK
// was already known to be dead.
let _tdInitFailed = false;
// ONE shared promise that every caller races against, created lazily and rejected once when the SDK
// reports failure. It must not be per-call: a per-call promise pushed its rejector into a module-level
// array that was never pruned when the racing promise won, so each getTronDesignerAsync call leaked a
// rejector (and the closures it captured) for the lifetime of the page.
let _tdInitFailurePromise: Promise<never> | null = null;
let _rejectTdInitFailure: ((err: Error) => void) | null = null;

function tdInitFailure(): Promise<never> {
    if (_tdInitFailed) {
        return Promise.reject(new Error("TronDesigner is unavailable (initialization failed)"));
    }
    if (!_tdInitFailurePromise) {
        _tdInitFailurePromise = new Promise<never>((_, reject) => { _rejectTdInitFailure = reject; });
        // Nothing may await this promise directly; it only ever loses or rejects a race. Attaching a
        // no-op handler keeps it from surfacing as an unhandled rejection when it does reject.
        _tdInitFailurePromise.catch(() => { /* consumed by the racing callers */ });
    }
    return _tdInitFailurePromise;
}

export function getTronDesignerAsync(): Promise<IWebsiteIntegration> {
    // TD is off for this shop (no SDK <script> rendered — see isTronDesignerActive). window.tronDesigner
    // can then never appear, so polling out the full ready window would only end in the same rejection
    // several seconds later. Fail immediately and mark the reason so callers can stay silent about it.
    if (!isTronDesignerActive()) {
        return Promise.reject(tronDesignerInactiveError());
    }

    return Promise.race([tdInitFailure(), new Promise<IWebsiteIntegration>((resolve, reject) => {
        let attempts = 0;

        const poll = () => {
            const td = window.tronDesigner;
            if (td !== undefined) {
                // window.tronDesigner can be set before initialization finishes; the designer can only be
                // opened once initialized, so wait for getInitializing() to resolve before handing it back.
                // Race it against a timeout: a broken/hung SDK can leave getInitializing() pending forever,
                // which would otherwise hang every awaiting caller indefinitely (see TD_INIT_TIMEOUT_MS).
                let settled = false;
                const initTimeout = window.setTimeout(() => {
                    if (settled) return;
                    settled = true;
                    reject(new Error("TronDesigner initialization timed out"));
                }, TD_INIT_TIMEOUT_MS);
                td.getInitializing()
                    .then(() => {
                        if (settled) return;
                        settled = true;
                        window.clearTimeout(initTimeout);
                        resolve(td);
                    })
                    .catch(err => {
                        if (settled) return;
                        settled = true;
                        window.clearTimeout(initTimeout);
                        reject(err);
                    });
                return;
            }

            attempts++;
            if (attempts >= TD_READY_MAX_ATTEMPTS) {
                reject(new Error("TronDesigner is not available"));
                return;
            }

            window.setTimeout(poll, TD_READY_POLL_INTERVAL_MS);
        };

        window.setTimeout(poll, TD_READY_POLL_INTERVAL_MS);
    })]);
}

export function getPendingFreePrintJobGuid(): string | null {
    return getPendingFreeDesign()?.printJobGuid ?? null;
}

export function clearPendingFreePrintJobGuid(): void {
    try { window.sessionStorage.removeItem(PENDING_FREE_PJ_KEY); }
    catch { /* ignore */ }
}

const PRINT_SET_CART_ITEM_ID_KEY = "printSetCartItemId";

function getPrintSetCartItemIdFromEvent(event: DesignerEntityEvent<IPrintJobDto>): string | null {
    // Prefer the live id captured when the editor was opened (see _activeCartPrintSetItemId). The ids on the
    // event are echoed from the print job's persisted state and are stale when editing an existing job.
    if (_activeCartPrintSetItemId) {
        return _activeCartPrintSetItemId;
    }

    const custom = event.detail?.customProperties;
    if (custom) {
        const id = custom[PRINT_SET_CART_ITEM_ID_KEY] ?? custom["printSetOrderItemGuid"];
        if (id) {
            return String(id);
        }
    }

    return event.detail?.customer?.orderItemCode ?? null;
}

// ---- Shared-cart print job cloning ---------------------------------------------------------------
// A cart taken over from a shared cart carries the SENDER's print job guid as data-td-pending-clone on
// each print set (stamped by SharedCartService). The recipient must get an independent COPY so their
// edits never touch the sender's design — and the server has no TD client, so the duplication runs
// here: td.clonePrintJob (TD PR 4258) creates a full copy including images, which is then linked
// through the normal link endpoint (linking clears the pending flag server-side). Failures leave the
// flag in place so the next cart render retries; the customer creating their own design settles it too.
// Returns true when at least one clone was linked — the caller should refresh the cart.
export async function processPendingPrintJobClones(): Promise<boolean> {
    const pendingSets = Array.from(
        document.querySelectorAll<HTMLElement>(".cart-preview-printing-set[data-td-pending-clone]")
    ).filter(el => !el.dataset.tdPrintJobGuid && !el.dataset.tdCloneInFlight);

    if (!pendingSets.length) {
        return false;
    }

    let td: IWebsiteIntegration;
    try {
        td = await getTronDesignerAsync();
    } catch (err) {
        devLog("TronDesigner: unavailable — shared-cart design clone deferred", err);
        return false;
    }

    // clonePrintJob shipped with TD PR 4258 and is not in the published 1.5.0 types — feature-detect so
    // an older TD instance defers the clone instead of throwing.
    const clonePrintJob = (td as unknown as { clonePrintJob?: (guid: string) => Promise<string | undefined> }).clonePrintJob;
    if (typeof clonePrintJob !== "function") {
        devLog("TronDesigner: clonePrintJob not available on this TD instance — shared-cart design clone deferred");
        return false;
    }

    let anyLinked = false;
    for (const el of pendingSets) {
        const sourceGuid = el.dataset.tdPendingClone;
        const cartItemId = el.dataset.cartItemId;
        if (!sourceGuid || !cartItemId) {
            continue;
        }
        el.dataset.tdCloneInFlight = "1";
        try {
            const cloneGuid = await clonePrintJob.call(td, sourceGuid);
            if (!cloneGuid) {
                console.error("TronDesigner: clonePrintJob returned no guid", { sourceGuid, cartItemId });
                continue;
            }
            // No design-config body: the server snapshots the current calculator config as the §4.4
            // baseline — correct here, since the shared calculation IS the config the design was made for.
            await linkPrintJobToCartItem(cartItemId, cloneGuid);
            anyLinked = true;
        } catch (err) {
            console.error("TronDesigner: shared-cart design clone failed", { sourceGuid, cartItemId, err });
        } finally {
            delete el.dataset.tdCloneInFlight;
        }
    }

    return anyLinked;
}

async function linkPrintJobToCartItem(printSetCartItemId: string, printJobGuid: string, designConfig: DesignMotiveConfig[] = [], designVariants: DesignVariantConfig[] = []): Promise<void> {
    devLog("TronDesigner: POST print job link", { printSetCartItemId, printJobGuid, designConfig, designVariants });
    await new HttpClient().fetch(
        `/api/cart/${encodeURIComponent(printSetCartItemId)}/printjob/${encodeURIComponent(printJobGuid)}`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ motives: designConfig, variants: designVariants }),
        }
    );
}

// Design-First (Scenario 2): if a free-mode design was saved before the cart item existed, its guid was
// stashed in sessionStorage. Once the customer has gone through TCC and the cart item exists, link it to
// the (single) unlinked print set. One-shot: the pending guid is consumed regardless of outcome so it can
// never be mis-linked on a later, unrelated cart visit. Returns true if a link was performed (the caller
// should then refresh the cart so the print set shows its linked state).
export async function linkPendingFreeDesignToCart(): Promise<boolean> {
    const pending = getPendingFreeDesign();
    if (!pending?.printJobGuid) {
        return false;
    }

    clearPendingFreePrintJobGuid();

    // Unlinked print sets render a "Create Design" action (data-td-cart-action="create"). Only auto-link
    // when there is exactly one candidate — with several we cannot tell which one the design belongs to
    // (design-first with a single product is the supported case, §3.2.2).
    const createButtons = Array.from(
        document.querySelectorAll<HTMLElement>('[data-td-cart-action="create"][data-cart-item-id]')
    );
    if (createButtons.length !== 1) {
        devLog("TronDesigner: pending free design not auto-linked", { candidates: createButtons.length });
        return false;
    }

    const cartItemId = createButtons[0].dataset.cartItemId;
    if (!cartItemId) {
        return false;
    }

    try {
        await linkPrintJobToCartItem(cartItemId, pending.printJobGuid, pending.designConfig ?? [], pending.designVariants ?? []);
        devLog("TronDesigner: linked pending free design to cart item", { cartItemId, printJobGuid: pending.printJobGuid });
        return true;
    } catch (err) {
        console.error("TronDesigner: failed to link pending free design to cart item:", err);
        return false;
    }
}

function subscribeGlobalEvents(): void {
    // initTronDesigner runs again on every product-variant reload; without this guard each reload adds
    // another subscription pair, so one save would fire handlePrintJobCreated (and the cart-link POST) N times.
    if (_globalEventsBound) return;
    // Latch immediately so overlapping init calls can't double-subscribe while the SDK wait is pending —
    // but UNLATCH on failure: if the SDK only becomes ready after waitForTronDesigner's poll window, the
    // printJob subscriptions were silently never bound and a later save would never be linked (silent data
    // loss). Resetting the flag lets the next openDesignerDialog()/init retry the binding.
    _globalEventsBound = true;

    getTronDesignerAsync()
        .then(td => {
            td.subscribeDesignerEvent("printJob", "created", (event: DesignerEntityEvent<IPrintJobDto>) => {
                handlePrintJobCreated(event);
            });

            td.subscribeDesignerEvent("printJob", "updated", (event: DesignerEntityEvent<IPrintJobDto>) => {
                handlePrintJobUpdated(event);
            });
        })
        .catch(err => {
            _globalEventsBound = false;
            if (isTronDesignerInactiveError(err)) {
                return;
            }
            console.error("TronDesigner: binding global print-job events failed — will retry on the next editor open.", err);
        });
}

async function handlePrintJobCreated(event: DesignerEntityEvent<IPrintJobDto>): Promise<void> {
    const printJobGuid = event.detail?.printJobGuid;
    if (!printJobGuid) return;
    _saveSeq++; // a real save event arrived — disarms the Confirmed-close reload fallback

    const printSetCartItemId = getPrintSetCartItemIdFromEvent(event);
    const designConfig = extractDesignConfig(event);
    const designVariants = extractDesignVariants(event);

    if (_activeIndependentMode) {
        // Independent proofreading (§1.2 option 1): deliberately never linked to a cart item. Remember the
        // job for the session so the product-detail button flips to "Edit design" (§1.3) — and do NOT stash
        // it as a pending cart link, or a later calculator flow would wrongly attach it to a print set.
        if (_activeIndependentStoreKey) {
            setIndependentPrintJobGuid(_activeIndependentStoreKey, printJobGuid);
            refreshProductDetailDesignButtonLabel();
        }
    } else if (printSetCartItemId) {
        try {
            // AWAIT the link before signalling: the save event is what the cart listens on to refresh, and it
            // must not fire until the print job is actually linked (otherwise the cart reloads still-unlinked).
            await linkPrintJobToCartItem(printSetCartItemId, printJobGuid, designConfig, designVariants);
        } catch (err) {
            console.error("TronDesigner Failed to link print job to cart item:", err);
        }
    } else {
        // Design-First: there is no cart item yet. Persist the guid + design config across the TCC round-trip;
        // linked to the new print set on the cart page by linkPendingFreeDesignToCart() (called from cart init).
        setPendingFreeDesign({ printJobGuid, designConfig, designVariants });
    }

    document.dispatchEvent(new CustomEvent("tronDesignerPrintJobSaved", {
        bubbles: false,
        detail: { printJobGuid, cartItemId: printSetCartItemId ?? null }
    }));
}

async function handlePrintJobUpdated(event: DesignerEntityEvent<IPrintJobDto>): Promise<void> {
    const printJobGuid = event.detail?.printJobGuid;
    if (!printJobGuid) return;
    _saveSeq++; // a real save event arrived — disarms the Confirmed-close reload fallback

    const printSetCartItemId = getPrintSetCartItemIdFromEvent(event);

    if (_activeIndependentMode) {
        // Independent proofreading: keep the session record fresh (the guid is stable across updates, but
        // this also covers a job re-saved after the store was cleared) and never attempt a cart link.
        if (_activeIndependentStoreKey) {
            setIndependentPrintJobGuid(_activeIndependentStoreKey, printJobGuid);
            refreshProductDetailDesignButtonLabel();
        }
        document.dispatchEvent(new CustomEvent("tronDesignerPrintJobSaved", {
            bubbles: false,
            detail: { printJobGuid, cartItemId: null }
        }));
        return;
    }

    // Re-link on update (same as create) so the cart re-snapshots the config the design is now saved with.
    // Without this, fixing a mismatched design in the editor would not clear the warning. Await before the
    // save event so the cart refresh below sees the updated link.
    if (printSetCartItemId) {
        try {
            await linkPrintJobToCartItem(printSetCartItemId, printJobGuid, extractDesignConfig(event), extractDesignVariants(event));
        } catch (err) {
            console.error("TronDesigner Failed to re-link updated print job to cart item:", err);
        }
    }

    document.dispatchEvent(new CustomEvent("tronDesignerPrintJobSaved", {
        bubbles: false,
        detail: {
            printJobGuid,
            cartItemId: printSetCartItemId ?? null,
        }
    }));
}


function waitForTronDesigner(onReady: (td: IWebsiteIntegration) => void): void {
    getTronDesignerAsync()
        .then(onReady)
        .catch(err => {
            if (isTronDesignerInactiveError(err)) {
                devLog("TronDesigner: SDK not enabled for this shop — skipping", err);
                return;
            }
            console.error("TronDesigner: could not open the editor —", err);
            devLog("TronDesigner: getTronDesignerAsync failed", err);
        });
}

// The shop's current language as an ISO-2 code, taken from <html lang="..."> (set per shop language).
// Used for the editor's print-job language and the customer's language. Undefined when not set.
function getShopLanguageCode(): string | undefined {
    const lang = document.documentElement.lang?.trim().toLowerCase();
    return lang ? lang : undefined;
}

// The SDK <script> element (carries data-td-api-key plus the customer identity attributes emitted by
// TronDesignerHelper).
function getTronDesignerScriptEl(): HTMLScriptElement | null {
    return document.querySelector<HTMLScriptElement>('script[data-td-api-key]');
}

// TD is active for this shop ONLY when TronDesignerHelper rendered the SDK <script> (settings IsActive +
// AllowEditorFrontend). Shops running TronLogo only get no script, so the SDK global never appears — the
// eager inits below must not wait for it there (they used to log two console errors on every cart render).
// The gate can never disable a working TD: without that <script> the SDK is unreachable by definition.
export function isTronDesignerActive(): boolean {
    return getTronDesignerScriptEl() !== null;
}

// Marker on the "TD is not enabled here" rejection so callers can skip the console.error — this is an
// expected state on a non-TD shop, not a failure worth reporting.
const TD_INACTIVE_FLAG = "tdInactive";

function tronDesignerInactiveError(): Error {
    const err = new Error("TronDesigner is not enabled for this shop");
    (err as Error & { [TD_INACTIVE_FLAG]?: boolean })[TD_INACTIVE_FLAG] = true;
    return err;
}

function isTronDesignerInactiveError(err: unknown): boolean {
    return !!(err as { [TD_INACTIVE_FLAG]?: boolean })?.[TD_INACTIVE_FLAG];
}

// The logged-in customer's identity (PersonGUID / email / name / company), stamped on the SDK <script> as
// data-td-customer-* by TronDesignerHelper. Returned as the per-print-job `customer` argument so the identity
// is recorded ON the print job (not only pushed globally via setCustomer, which does not write the PJ's own
// customer record). Empty object for anonymous visitors.
function readCustomerIdentity(): Pick<ArgCustomer, "customerIdentity" | "customerEmail" | "customerPerson" | "customerCompany"> {
    const script = getTronDesignerScriptEl();
    if (!script) return {};
    return {
        ...(script.dataset.tdCustomerIdentity ? { customerIdentity: script.dataset.tdCustomerIdentity } : {}),
        ...(script.dataset.tdCustomerEmail ? { customerEmail: script.dataset.tdCustomerEmail } : {}),
        ...(script.dataset.tdCustomerPerson ? { customerPerson: script.dataset.tdCustomerPerson } : {}),
        ...(script.dataset.tdCustomerCompany ? { customerCompany: script.dataset.tdCustomerCompany } : {}),
    };
}

// The webshop tracking guid (Logs.Visit), exposed by TronDesignerHelper as data-td-tracking-id. Stable per
// visitor incl. guests — the §4.3 trackingID that lets a print job be traced back to its webshop session.
function getWebshopTrackingId(): string | undefined {
    return getTronDesignerScriptEl()?.dataset.tdTrackingId || undefined;
}

// Assemble the print job's customProperties (§4.3). Beyond the required trackingID we add as much diagnostic
// context as each open path can supply — calculation guid, cart/product ids, product codes — so a print job
// can be traced to the webshop session / cart / product that created it when reviewing a proofreading or
// resolving a support ticket. Only non-empty string values survive (customProperties is Record<string,string>).
// PRINT_SET_CART_ITEM_ID_KEY is preserved verbatim as the cart re-link key (see getPrintSetCartItemIdFromEvent).
function buildCustomProperties(extra: Record<string, string | number | null | undefined> = {}): Record<string, string> {
    const identity = readCustomerIdentity();
    const base: Record<string, string | number | null | undefined> = {
        trackingID: getWebshopTrackingId(),          // §4.3 — guid of the webshop visitor/session
        customerIdentity: identity.customerIdentity,  // guid of the customer in TD (duplicated here for support)
        languageCode: getShopLanguageCode(),
        pageUrl: window.location.href,
        ...extra,
    };

    const result: Record<string, string> = {};
    for (const key of Object.keys(base)) {
        const value = base[key];
        if (value === null || value === undefined) continue;
        const text = String(value).trim();
        if (text) result[key] = text;
    }
    return result;
}

// The §4.1 variant code/name overrides — the shop-facing product code + product title shown to the customer,
// so the proofreading matches the product page instead of exposing the supplier code (§4.2). Undefined when
// neither is known (so the variant argument stays clean).
function buildProductOverrides(shopProductCode?: string, title?: string): { productCode?: string; productName?: string } | undefined {
    const overrides: { productCode?: string; productName?: string } = {};
    if (shopProductCode) overrides.productCode = shopProductCode;
    if (title) overrides.productName = title;
    return (overrides.productCode || overrides.productName) ? overrides : undefined;
}

// Push the logged-in customer's identity to TD via the documented setCustomer() API (the same mechanism
// TronManager uses). The identity is transported on the SDK <script> as data-td-customer-* attributes by
// TronDesignerHelper; passing it on the script tag alone does NOT reach TD, so we forward it here once the
// SDK is ready. No-op for anonymous visitors with no language.
function pushCustomerIdentity(): void {
    const customer = {
        ...readCustomerIdentity(),
        customerLanguage: getShopLanguageCode(),
    };

    if (!customer.customerIdentity && !customer.customerEmail && !customer.customerPerson && !customer.customerLanguage) {
        return;
    }

    waitForTronDesigner(td => {
        void td.setCustomer(customer).catch(err => console.error("TronDesigner setCustomer failed:", err));
    });
}

// Disable the editor entry points when the SDK fails to initialise (e.g. bad api key) so clicks don't appear
// to do nothing. Wired as data-td-script-init-failed-callback on the SDK <script> by TronDesignerHelper.
function disableTronDesignerEntryPoints(): void {
    // Customer-facing reason for the disabled state — the shopper cannot activate TD, so this explains the
    // outage rather than instructing them to enable it (that admin-side wording lives in the admin portal).
    const unavailable = localize("TdEditorUnavailable");
    const link = document.querySelector('#enter-tron-designer');
    link?.setAttribute('aria-disabled', 'true');
    if (unavailable) {
        link?.setAttribute('title', unavailable);
    }
    document.querySelectorAll<HTMLButtonElement>('[data-td-cart-action]').forEach(btn => {
        btn.disabled = true;
        if (unavailable) {
            btn.title = unavailable;
        }
    });
    // The cart logo-gallery trigger is a <label>, not a <button> — it opens the same SDK, so it must be
    // neutralised too or it keeps spinning against a dead SDK. pointer-events:none stops the click;
    // aria-disabled + the modifier class carry the state to AT and styling.
    document.querySelectorAll<HTMLElement>('[data-td-logo-action]').forEach(el => {
        el.setAttribute('aria-disabled', 'true');
        el.classList.add('cart-card__logo-btn--disabled');
        el.style.pointerEvents = 'none';
        if (unavailable) {
            el.setAttribute('title', unavailable);
        }
    });
    // The proofreading download also goes through the SDK (getProofreading) — with a dead SDK the click
    // would just spin against the init timeout, so neutralise it like the other entry points above.
    document.querySelectorAll<HTMLElement>('[data-td-download-proofreading]').forEach(el => {
        el.setAttribute('aria-disabled', 'true');
        el.style.pointerEvents = 'none';
        if (unavailable) {
            el.setAttribute('title', unavailable);
        }
    });

    // A VISIBLE state, not just disabled buttons: every cart printing group gets a warning line under
    // its head (same styling as the design hints) saying the design service is down and the designs are
    // safe — hover tooltips alone left the outage looking like silently broken buttons.
    const bannerText = localize("TdServiceUnavailableCart");
    if (bannerText) {
        document.querySelectorAll<HTMLElement>(".cart-card__printing").forEach(section => {
            if (section.querySelector("[data-td-unavailable-banner]")) {
                return;
            }
            const hint = document.createElement("div");
            hint.className = "cart-card__design-hint cart-card__design-hint--error";
            hint.setAttribute("data-td-unavailable-banner", "1");
            hint.textContent = bannerText;
            const head = section.querySelector(".cart-card__section-head");
            if (head) {
                head.insertAdjacentElement("afterend", hint);
            } else {
                section.prepend(hint);
            }
        });
    }
}

window.tronDesignerScriptInitFailed = (error?: unknown) => {
    console.error("TronDesigner SDK failed to initialise — disabling editor entry points.", error);
    devLog("TronDesigner: script init failed", error);
    // Fail every pending/future getTronDesignerAsync immediately — no waiter should sit out the 15s
    // init timeout (cart design skeletons, proofreading clicks) once the SDK is known dead.
    _tdInitFailed = true;
    _rejectTdInitFailure?.(new Error("TronDesigner is unavailable (initialization failed)"));
    _rejectTdInitFailure = null;
    disableTronDesignerEntryPoints();
};

// Single entry point for opening the editor in a dialog. Adds an onClose fallback: a Confirmed close must be
// followed by a printJob created/updated event (which links the design and refreshes the cart). If that event
// never arrives, reload so the cart can't get stuck showing an unlinked print set. (Mirrors TronManager's
// post-Confirmed fallback timer.) The fallback only fires on a genuinely missing event — a normal save bumps
// _saveSeq within milliseconds and disarms it.
function openDesignerDialog(td: IWebsiteIntegration, args: EditorArguments, independent: { storeKey: string | null } | null = null): void {
    // Retry the global printJob subscriptions if an earlier attempt failed (the SDK is demonstrably ready
    // here, so this succeeds) — without it a save from this very editor would never be linked.
    subscribeGlobalEvents();

    // The editor bundle (trondesigner-editor.js) calls crypto.randomUUID(), which exists ONLY in a secure
    // context. Over plain http:// (test hosts served without TLS) the bundle throws while evaluating, the
    // SDK then logs "cannot open, trondesigner-editor is not defined" and openDesigner returns without
    // opening anything — the click looks silently dead. Say so instead of leaving it unexplained.
    if (!window.isSecureContext) {
        console.error(
            "TronDesigner: the editor cannot run on this origin — it requires a secure context (https). " +
            "Open the shop over https and try again."
        );
        displayError(localize("TdEditorUnavailable"));
        return;
    }
    // Capture the live print-set cart-item id we're opening against (absent for product-detail design-first).
    // This is the single funnel all open paths go through, so the re-link handlers can rely on it instead of
    // the editor's stale event echo. See _activeCartPrintSetItemId.
    const captured = args.printJob?.customProperties?.[PRINT_SET_CART_ITEM_ID_KEY];
    _activeCartPrintSetItemId = captured != null ? String(captured) : null;
    // Independent-mode marker for the save handlers; reset on every open so a later cart/calculator open
    // of the editor can never be mistaken for an independent one. See _activeIndependentMode.
    _activeIndependentMode = independent != null;
    _activeIndependentStoreKey = independent?.storeKey ?? null;

    void logProductEvent({
        type: ProductEventType.OpenDesign,
        // Product-detail opens: guids from the page. Cart opens usually have no product-detail root —
        // shop/session/tracking still land server-side; CreateDesign (on first SetPrintJobGuid) carries product.
        ...readProductDetailStatisticGuids(),
        printJobGuid: args.printJob?.printJobGuid,
        calculationGuid: args.printJob?.customProperties?.["calculationGuid"] as string | undefined,
    });

    const seqAtOpen = _saveSeq;
    const openResult = td.openDesigner(args, {
        mode: "dialog",
        onClose: (reason) => {
            if (reason !== "Confirmed") {
                return;
            }
            window.setTimeout(() => {
                if (_saveSeq === seqAtOpen) {
                    devLog("TronDesigner: Confirmed close with no save event — reloading to recover");
                    window.location.reload();
                }
            }, TD_SAVE_FALLBACK_MS);
        }
    });
    // CART-09: closing the SDK's logo pre-processing dialog rejects this promise with "User cancelled
    // logo processing dialog" — a deliberate user action, not an error, so it is dropped quietly. Any
    // OTHER rejection is a genuine open failure and stays visible in the console (it must not become a
    // silent no-op).
    Promise.resolve(openResult as unknown as Promise<void> | void).catch(err => {
        const message = err instanceof Error ? err.message : String(err ?? "");
        if (/cancel/i.test(message)) {
            devLog("TronDesigner: editor open cancelled by the user", err);
        } else {
            console.error("TronDesigner: opening the editor failed:", err);
        }
    });
}

export async function onDownloadPdf(printJobGuid: string, overlayTarget?: HTMLElement): Promise<void> {
    // Generating the proof PDF in TronDesigner can take a few seconds; show an overlay scoped to the print
    // group (or the button) so the click gives immediate feedback instead of looking unresponsive. Always
    // cleared in finally.
    const overlayElement = overlayTarget ?? document.body;
    LoadingIndication.show(overlayElement);
    try {
        const tronDesigner = await getTronDesignerAsync();
        const blob = await tronDesigner.getProofreading(printJobGuid);
        if (!blob) {
            return;
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `proofreading-${printJobGuid}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error("TronDesigner failed to download proofreading:", err);
        // Surface the failure to the customer — a console-only error left the click looking like a no-op.
        // Reuses the existing generic TD-failure resource (no new translation keys).
        displayError(localize("TdLogoGalleryFailed"));
    } finally {
        LoadingIndication.hide(overlayElement);
    }
}

function readProductData(): TronDesignerProductData {
    const el = getProductDetailMainElement();
    return {
        // data-supplier-guid = Product.SupplierGuid (the supplier/vendor identifier)
        supplierGuid: el?.dataset.supplierGuid ?? "",
        // data-td-model-code = Product.SupplierModelCode
        modelCode: el?.dataset.tdModelCode ?? "",
        // data-product-supplier-code = Product.SupplierProductCode (the supplier's product/variant code — the
        // namespace the editor works in; NOT what is shown to the customer, see the overrides below)
        productCode: el?.dataset.productSupplierCode ?? "",
        // data-product-id / -title / -shop-code = the product-page identity used for the §4.1 proofreading data
        productId: el?.dataset.productId ?? "",
        // data-product-title = Product.Name (the title as shown on the product page)
        title: el?.dataset.productTitle ?? "",
        // data-product-shop-code = the shop-facing code resolved by ProductCodesPriority (§4.1/§4.2)
        shopProductCode: el?.dataset.productShopCode ?? ""
    };
}

/** Product-detail guids for StatisticsService OpenDesign (same model/variant rule as visit tracking). */
function readProductDetailStatisticGuids(): { productModelGuid?: string; productVariantGuid?: string } {
    const el = getProductDetailMainElement();
    const modelGuid = el?.dataset.modelGuid || undefined;
    const productGuid = el?.dataset.productGuid || undefined;
    if (!modelGuid && !productGuid) {
        return {};
    }
    const productModelGuid = modelGuid || productGuid;
    const productVariantGuid = modelGuid && productGuid && modelGuid !== productGuid
        ? productGuid
        : undefined;
    return { productModelGuid, productVariantGuid };
}

// The customer's currently selected TronLogo (a logo GUID), read the same way as cart.ts / tron-calculator.ts.
function getCurrentTronLogoGuid(): string | null {
    return window?.tron?.User?.getUserLogo() || null;
}

// The shadow-TL anonymous user GUID (owner of the uploaded logos), exposed by the TronLogo engine.
// The guid is stable per browser, but the engine is not on every page (TD-first shops never load it
// on the cart) — so every sighting is cached and the cache serves as the fallback. This is what lets
// the cart render TL logos picked earlier on the product detail / TCC flow without a re-upload.
const TL_USER_GUID_CACHE_KEY = "tl_user_guid";

export function getCurrentTronLogoUserGuid(): string | null {
    const live = window?.tron?.User?.userGuid || null;
    if (live) {
        try { window.localStorage.setItem(TL_USER_GUID_CACHE_KEY, live); } catch { /* cache is best-effort */ }
        return live;
    }
    try {
        return window.localStorage.getItem(TL_USER_GUID_CACHE_KEY);
    } catch {
        return null;
    }
}

// Background sightings: pages where the TL engine loads late (async script) would miss the cache if
// nothing queried the guid — poll briefly on every page so a product-detail visit reliably stashes it.
export function rememberTlUserGuid(attempt = 0): void {
    if (window?.tron?.User?.userGuid) {
        getCurrentTronLogoUserGuid();
        return;
    }
    if (attempt < 20) {
        window.setTimeout(() => rememberTlUserGuid(attempt + 1), 500);
    }
}

// The TronLogo logo-upload API base URL ("{tlApiUrl}api/{tlApiKey}/"), exposed on the TD script tag by
// TronDesignerHelper when shadow TronLogo is active. Used to build the logo-upload file URL for the editor.
export function getTronLogoResourceBase(): string | null {
    const script = document.querySelector<HTMLScriptElement>('script[data-td-api-key]');
    return script?.dataset.tdTlLogoBase || null;
}

// Builds the TL logo download URL for the TD editor logo argument. The editor's `logo.guid` expects a
// *TronDesigner* logo, which a TronLogo guid is NOT — so we pass the original TronLogo file as a URL instead
// and TD downloads and converts it into a TD logo. This mirrors TronManager's setDefaultPrintJobLogo:
// {base}user/{userGuid}/logo-upload/{logoGuid}?color_variant=1221&size_variant=2021
//   - color_variant=1221 (OriginalsOriginal) → the original uploaded logo in its original colours
//   - size_variant=2021  (Undefined)         → native size (not a sized editor preview)
// Uses only the public api key (no private key/token).
export function buildTronLogoUrl(base: string, userGuid: string, logoGuid: string): string {
    return `${base}user/${encodeURIComponent(userGuid)}/logo-upload/${encodeURIComponent(logoGuid)}?color_variant=1221&size_variant=2021`;
}

// Resolves which TronLogo (if any) to auto-import into the TD editor (Addendum 2 §5.2 / §6.1):
//   1. the session-remembered TL-link (tlgd) logo — the pre-branded logo the customer arrived with is
//      carried into the design flow with priority (§5.2),
//   2. otherwise the TL engine's current logo (§6.1).
// Either candidate is imported only after the server confirms it resolves under the CURRENT user's TL
// download URL (isTlLogoImportable): that is the §6.1 ownership rule — a logo that cannot be confirmed as
// the user's own (e.g. remembered from another webshop/user context) is never imported silently — and it
// also stops the editor aborting on a logo URL TD could not fetch. Returns undefined when nothing qualifies.
async function resolveTronLogoEditorArg(): Promise<{ url: string } | undefined> {
    const userGuid = getCurrentTronLogoUserGuid();
    const base = getTronLogoResourceBase();
    if (!userGuid || !base) {
        return undefined;
    }

    const candidates = [getTlLinkLogoGuid(), getCurrentTronLogoGuid()];
    for (const guid of candidates) {
        if (!guid) {
            continue;
        }
        if (await isTlLogoImportable(userGuid, guid)) {
            return { url: buildTronLogoUrl(base, userGuid, guid) };
        }
        devLog("TronDesigner: TL logo not confirmed as importable — skipped", { guid });
    }

    return undefined;
}

type EditorButtonStyle = "Primary" | "Secondary";
type EditorButtonFunction =
    | "FinalConfirm"
    | "SaveAndClose"
    | "Save"
    | "GenerateProofreading"
    | "DownloadPreviewImage"
    | "DownloadVisualization"
    | "DownloadAllPreviews"
    | "DownloadAllVisualizations";
type EditorButton = {
    style: EditorButtonStyle;
    function: EditorButtonFunction;
    labels: Array<{ language: string; text: string }>;
};
type ArgEditorWithButtons = NonNullable<EditorArguments["editor"]> & {
    buttons?: EditorButton[];
};

// §1.2 option 1: an independent proofreading must not continue into a purchase/cart flow, so the editor's
// post-confirm "Continue" step has to go. resultAction "Close" is the lever the SDK exposes for that: the
// final confirm simply closes the editor (back to the product page) instead of showing a Continue dialog.
function independentEditorResult(): { editor: ArgEditorWithButtons } {
    return {
        editor: {
            result: { resultAction: "Close" },
            buttons: [independentProofreadingButton()],
        },
    };
}

function independentProofreadingButton(): EditorButton {
    const language = getShopLanguageCode() ?? "en";
    const text = localize("TdGetEProof");
    const labels = [{ language: "en", text: "Get E-proof" }];
    if (language !== "en") {
        labels.push({ language, text });
    }
    return { style: "Secondary", function: "GenerateProofreading", labels };
}

export function openDesignerFree(product: TronDesignerProductData, independent?: { storeKey: string | null }): void {
    waitForTronDesigner(async td => {
        // NOTE: the builder's .open() throws "Method not implemented" for the partial-product path, so we
        // build complete EditorArguments and call the integration's primary openDesigner() instead.
        // - editorOptions is required.
        // - the model must be nested under model.modelCode (ArgAddProduct has no top-level modelCode).
        // - supplier is identified by GUID (TronShop has no vendor "supplier code"); SupplierProductCode is
        //   the product/variant code, preselected here so the editor opens on the viewed variant.
        // - logo: pre-load the customer's TronLogo — tlgd link logo first, then the current one, both only
        //   when server-verified as importable for this user (§5.2/§6.1, see resolveTronLogoEditorArg).
        const logo = await resolveTronLogoEditorArg();
        // §4.1 — carry the product-page title and the shop-facing code so the proofreading matches the page.
        const overrides = buildProductOverrides(product.shopProductCode, product.title);
        const args: EditorArguments & { editor?: ArgEditorWithButtons } = {
            openMode: "Create",
            dataMode: "Partial",
            editorOptions: {
                showSelectedVariants: "All",
                showSelectedPrintOptions: "All"
            },
            ...(independent ? independentEditorResult() : {}),
            printJob: {
                languageCode: getShopLanguageCode(),
                ...(product.title ? { title: product.title } : {}),
                addProducts: {
                    supplierGuid: product.supplierGuid,
                    model: { modelCode: product.modelCode, ...(product.title ? { title: product.title } : {}) },
                    variants: product.productCode ? [{ productCode: product.productCode, ...(overrides ? { overrides } : {}) }] : []
                },
                ...(logo ? { logo } : {}),
                customer: readCustomerIdentity(),
                customProperties: buildCustomProperties({
                    productId: product.productId,
                    productCode: product.shopProductCode,
                    supplierProductCode: product.productCode,
                    modelCode: product.modelCode,
                    supplierGuid: product.supplierGuid,
                    mode: independent ? "independent-proofreading" : "product-detail-design",
                })
            }
        };

        openDesignerDialog(td, args, independent ?? null);
    });
}

// DF-06: reopen the session's INDEPENDENT design in FREE mode. Unlike cart/order edits
// (openDesignerLocked — the job's configuration is priced and fixed), a design-first job has no priced
// configuration, so the customer must still be able to add/change print options when editing it. Edit +
// Partial + the product MODEL context re-arms the free-mode UI; the job's own variants/printings load
// from the job itself (no variant re-add — that could duplicate them).
// NOTE (F-19): the TD edit_mode reference documents guid-only Edits; the earlier crash was
// editorOptions:"Selected" with NO product context. Edit+Partial+model needs one verification pass with
// the TD team on dev.
function openDesignerEditFreeIndependent(printJobGuid: string, product: TronDesignerProductData, independent: { storeKey: string | null }): void {
    waitForTronDesigner(td => {
        const args = {
            openMode: "Edit",
            dataMode: "Partial",
            editorOptions: {
                showSelectedVariants: "All",
                showSelectedPrintOptions: "All"
            },
            ...independentEditorResult(),
            printJob: {
                languageCode: getShopLanguageCode(),
                printJobGuid,
                addProducts: {
                    supplierGuid: product.supplierGuid,
                    model: { modelCode: product.modelCode, ...(product.title ? { title: product.title } : {}) },
                    variants: []
                },
                customer: readCustomerIdentity(),
                customProperties: buildCustomProperties({
                    productId: product.productId,
                    productCode: product.shopProductCode,
                    supplierProductCode: product.productCode,
                    modelCode: product.modelCode,
                    supplierGuid: product.supplierGuid,
                    mode: "independent-proofreading",
                })
            }
        } as unknown as EditorArguments;

        openDesignerDialog(td, args, independent);
    });
}

export function openDesignerLocked(printJobGuid: string, printSetCartItemId?: string, calculationGuid?: string, independent?: { storeKey: string | null }): void {
    waitForTronDesigner(td => {
        // Open an existing print job in Edit mode via openDesigner (the builder's .open() is unreliable).
        // Edit opens by GUID only: the job already carries its product, variants, printings and mockup.
        // Per the TronDesigner reference (examples/edit_mode.html) and the SDK builder — whose setPrintJob()
        // returns only `.open()` — an Edit payload must NOT carry editorOptions/addProducts. We used to send
        // `editorOptions: { …: "Selected" }`; with no product context to select from, the editor resolved no
        // variant, so its mockup background came back undefined and it crashed in setBackgroundImage(null).
        // customer/customProperties stay — TD accepts and echoes them on Edit, and openDesignerDialog reads
        // printSetCartItemId back out of customProperties to relink the saved job to the cart item. Cast
        // because the SDK type marks editorOptions required although the runtime (and the reference) omit it.
        const args = {
            openMode: "Edit",
            ...(independent ? independentEditorResult() : {}),
            printJob: {
                languageCode: getShopLanguageCode(),
                printJobGuid,
                // Stamp the customer identity onto the job; orderItemCode carries the live cart print-set id
                // (the cart re-link target) — it is NOT the order code (§4.3: the order code is reserved for the
                // real order number, and there is no order at design time; the calculation guid goes to custom data).
                customer: {
                    ...readCustomerIdentity(),
                    ...(printSetCartItemId ? { orderItemCode: printSetCartItemId } : {})
                },
                customProperties: buildCustomProperties({
                    [PRINT_SET_CART_ITEM_ID_KEY]: printSetCartItemId,
                    calculationGuid,
                })
            }
        } as unknown as EditorArguments;

        openDesignerDialog(td, args, independent ?? null);
    });
}

// The session-store key identifying "the product" for independent print jobs (§1.3). Model-level, so
// switching variants of the same model keeps offering the same design; product id as the fallback for
// products without a supplier model code.
function getIndependentPrintJobStoreKey(): string | null {
    const el = getProductDetailMainElement();
    return el?.dataset.tdModelCode || el?.dataset.productId || null;
}

// §1.1 — the product-detail button reads "Create design" until a current-session independent print job
// exists for the product, then "Edit design". The localized labels ride on the button as data attributes
// (the server cannot know the client-session state, so it always renders the "Create design" default).
export function refreshProductDetailDesignButtonLabel(): void {
    const buttonEl = document.querySelector<HTMLAnchorElement>('#enter-tron-designer');
    if (!buttonEl) {
        return;
    }

    // A guest's inactive button never flips Create/Edit and carries a tooltip child span — writing
    // textContent here would wipe it. (Guests can't save a design, so this is belt-and-braces.)
    if (buttonEl.getAttribute('aria-disabled') === 'true') {
        return;
    }

    const storeKey = getIndependentPrintJobStoreKey();
    const hasIndependentDesign = !!(storeKey && getIndependentPrintJobGuid(storeKey));
    const label = hasIndependentDesign ? buttonEl.dataset.tdLabelEdit : buttonEl.dataset.tdLabelCreate;
    if (label) {
        buttonEl.textContent = label;
    }
}

// The single product-detail chooser (§1.2/§1.3 merged): "Design & order" vs "Design preview PDF".
// When a current-session independent design exists, the preview card offers "Edit previous design" /
// "Start a new design" instead of one click target — the order path stays visible either way.
function openStartDesignDialog(storeKey: string | null): Promise<void> {
    const existingPrintJobGuid = storeKey ? getIndependentPrintJobGuid(storeKey) : null;
    // "Design & order" replays a click on #enter-calculator (the TCC/price flow). That control is only
    // rendered for products that HAVE a price/calculator panel — it is absent on no-price / enquiry-only
    // products and on out-of-stock-blocked products (which show a Contact-us block instead). Offering the
    // order option there would be a dead click, so suppress it and leave only the preview/proofreading path.
    const hasOrderFlow = !!document.querySelector('#enter-calculator');

    // DF-00: with no order flow AND no session design there is exactly ONE choice — a chooser with a
    // single card is a pointless click. Skip the dialog and open the designer directly in design-only
    // (independent proofreading) mode; the loading overlay replaces the feedback the dialog used to give
    // while the SDK warms up.
    if (!hasOrderFlow && !existingPrintJobGuid) {
        const overlay = getProductDetailMainElement() ?? document.body;
        LoadingIndication.show(overlay);
        return getTronDesignerAsync()
            .then(() => openDesignerFree(readProductData(), { storeKey }))
            .finally(() => LoadingIndication.hide(overlay));
    }

    return openTronDesignerStartDialog({
        // "Design & order" is by definition identical to clicking "Calculate & Buy with printing"
        // (§1.2 option 2): TCC opens first and the design is created from the cart, linked to the
        // calculated item. Replaying the real click keeps the two entry points identical by construction
        // (same quantity handling in EnterCalculatorLink, same TCC arguments).
        onDesignAndOrder: () => {
            const calculatorLink = document.querySelector<HTMLAnchorElement>('#enter-calculator');
            if (!calculatorLink) {
                console.error("TronDesigner start dialog: #enter-calculator not found — cannot start the calculator flow.");
                return;
            }
            calculatorLink.click();
        },
        // A NEW independent proofreading print job, never connected to a cart item (§1.2 option 1).
        // Keep the dialog open (loading overlay) until the editor is ready. When it saves, it becomes
        // (or replaces) the remembered session design for this product.
        onPreviewPdf: async () => {
            await getTronDesignerAsync();
            openDesignerFree(readProductData(), { storeKey });
        },
        // "Edit previous design" — reopen the session's independent design (§1.3).
        onEditPrevious: existingPrintJobGuid
            ? async () => {
                const td = await getTronDesignerAsync();
                // The guid only lives in sessionStorage — the job may have been deleted on the TD side in
                // the meantime. When it can no longer be identified, forget it, flip the label back, and
                // continue straight into a fresh design (the least surprising recovery for this click).
                const printJob = await td.getPrintJob(existingPrintJobGuid).catch(() => null);
                if (!printJob) {
                    devLog("TronDesigner: session independent print job no longer exists", { printJobGuid: existingPrintJobGuid });
                    if (storeKey) {
                        clearIndependentPrintJob(storeKey);
                    }
                    refreshProductDetailDesignButtonLabel();
                    openDesignerFree(readProductData(), { storeKey });
                    return;
                }
                // DF-06: an independent design reopens FREE (add/change print options stays allowed) —
                // it has no priced configuration to lock against.
                openDesignerEditFreeIndependent(existingPrintJobGuid, readProductData(), { storeKey });
            }
            : undefined,
    }, { hasExistingDesign: !!existingPrintJobGuid, hasOrderFlow });
}

export function initTronDesignerProductDetailLink(): void {
    const buttonEl = document.querySelector('#enter-tron-designer') as HTMLAnchorElement;
    if (!buttonEl) {
        return;
    }

    if (buttonEl.getAttribute('aria-disabled') === 'true') {
        buttonEl.addEventListener('click', evt => evt.preventDefault());
        return;
    }

    refreshProductDetailDesignButtonLabel();

    // Guard against re-binding on repeated product-detail inits (variant reloads swap the element,
    // but initLoadedOrderControls can also run against an unchanged one).
    if (buttonEl.dataset.tdProductDetailBound) {
        return;
    }
    buttonEl.dataset.tdProductDetailBound = "1";

    buttonEl.addEventListener('click', evt => {
        evt.preventDefault();
        void openStartDesignDialog(getIndependentPrintJobStoreKey());
    });
}

export function initTronDesigner(): void {
    // Nothing to bind on a shop without the TD SDK (TronLogo-only shops); both calls below would just wait
    // out the SDK poll window and log an error on every cart/product render.
    if (!isTronDesignerActive()) {
        return;
    }

    subscribeGlobalEvents();
    pushCustomerIdentity();
}

interface CartDesignerEditorContext {
    printData: {
        printData: Array<{
            printPositionCode: string;
            printTechnologyCode: string;
            maxColors?: number;
            maxWidth?: number;
            maxHeight?: number;
            maxDiameter?: number;
            // The priced AREA tier in mm² (§0.3) — the real limit; maxWidth/maxHeight are only its
            // approximated bounding box. Validated natively by the editor since TD PR 4301.
            maxArea?: number;
        }>;
    };
    productCartItemId: string;
    calculationGuid: string;
    printJobGuid?: string;
    // TD logo picked via the cart's logo gallery (§2.1) — preloaded into the editor as a native TD logo.
    tronDesignerLogoGuid?: string;
    supplierGuid?: string;
    supplierCode?: string;
    modelCode?: string;
    // §4.1 — the product's display title and the shop-facing product code (resolved server-side by the shop's
    // ProductCodesPriority), used for the print job title and the variant code/name overrides.
    productTitle?: string;
    shopProductCode?: string;
    variants?: Array<{ productCode: string; quantity: number }>;
}

// ---- Edit-mode config sync (§4.4 reconciliation) -------------------------------------------------
// When a linked print job is reopened after the TCC calculation changed (variants added/removed,
// position/technology switched), the editor must receive the DELTA so the design catches up with what
// is actually priced. Additions ride the existing addProducts/addPrintings arguments; since TD PR 4281
// (merged 2026-07-17) an addProducts entry matching an EXISTING variant (by codeSupplier) updates that
// variant's quantity/overrides in place, and the removeProducts/removePrintings arguments are honored
// (removal codes not present in the job are a logged no-op, not an error). The removal args are not in
// the published @promotron/website-integration-types (latest 2.0.0) yet, so they stay typed locally —
// the shapes below match TD develop's ArgRemoveProduct/ArgRemovePrinting exactly.
type ArgRemovePrinting = { printPositionCode: string; printTechnologyCode: string };
type ArgPrintJobWithRemovals = EditorArguments["printJob"] & {
    removeProducts?: { variants: Array<{ productCode: string }> };
    removePrintings?: ArgRemovePrinting[];
};

type ArgConfigSyncVariant = {
    productCode: string;
    quantity?: number;
    overrides?: { productCode?: string; productName?: string };
};

interface EditorConfigSyncDelta {
    addVariants: ArgConfigSyncVariant[];
    // Existing job variants whose entered cart quantity differs — sent through addProducts, which TD
    // routes to its update path (quantity sync, CART-05/SET-05).
    updateVariants: ArgConfigSyncVariant[];
    removeVariants: Array<{ productCode: string }>;
    addPrintings: ArgAddPrinting[];
    removePrintings: ArgRemovePrinting[];
}

function tdCodeEquals(a?: string, b?: string): boolean {
    return (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
}

// Diff the LIVE print job against the current TCC config of the print set.
// - Variants compare the cart's supplier product code against the job's codeSupplier (codePtn as a
//   fallback so a matching variant is never mistaken for an addition).
// - Printings compare position+technology pairs (TCC codes vs the job's *Ptn codes — the same
//   namespace assumption as the server-side §4.4 check).
// Quantity-only changes propagate via updateVariants (TD PR 4281): an addProducts entry whose code
// matches an existing variant's codeSupplier updates its quantity in place, keeping the designed
// motive placement. Every add/update entry carries the §4.1 overrides — TD's update resets the
// shown code/title to the raw supplier values when overrides are absent (§4.2 leak).
function computeEditorConfigSyncDelta(context: CartDesignerEditorContext, printJob: IPrintJobDto): EditorConfigSyncDelta {
    const cartVariants = (context.variants ?? []).filter(v => !!v.productCode);
    const jobVariants = printJob.product?.variants ?? [];
    const overrides = buildProductOverrides(context.shopProductCode, context.productTitle);
    const toVariantArg = (cv: { productCode: string; quantity: number }): ArgConfigSyncVariant =>
        ({ productCode: cv.productCode, quantity: cv.quantity, ...(overrides ? { overrides } : {}) });

    const addVariants = cartVariants
        .filter(cv => !jobVariants.some(jv => tdCodeEquals(jv.codeSupplier, cv.productCode) || tdCodeEquals(jv.codePtn, cv.productCode)))
        .map(toVariantArg);

    // Quantity sync: only variants matched by codeSupplier — that is the namespace TD's update path
    // matches on. A variant matched only via the codePtn fallback is skipped: TD would treat the entry
    // as an ADDITION, fail the supplier-catalog lookup, and raise its fatal error dialog.
    const updateVariants = cartVariants
        .filter(cv => {
            if (!(cv.quantity > 0)) return false;
            const jv = jobVariants.find(j => tdCodeEquals(j.codeSupplier, cv.productCode));
            return !!jv && (jv.quantity ?? 0) !== cv.quantity;
        })
        .map(toVariantArg);

    let removeVariants = jobVariants
        .filter(jv => !cartVariants.some(cv => tdCodeEquals(jv.codeSupplier, cv.productCode) || tdCodeEquals(jv.codePtn, cv.productCode)))
        .map(jv => ({ productCode: jv.codeSupplier || jv.codePtn }))
        .filter(v => !!v.productCode);

    const cartPrintings = buildContextAddPrintings(context);
    const jobPrintings = (printJob.printings ?? [])
        .map(p => ({ position: p.printData?.positionCodePtn, technology: p.printData?.technologyCodePtn }))
        .filter(p => !!p.position && !!p.technology);

    const addPrintings = cartPrintings
        .filter(cp => !jobPrintings.some(jp => tdCodeEquals(jp.position, cp.printPositionCode) && tdCodeEquals(jp.technology, cp.printTechnologyCode)));

    let removePrintings: ArgRemovePrinting[] = jobPrintings
        .filter(jp => !cartPrintings.some(cp => tdCodeEquals(jp.position, cp.printPositionCode) && tdCodeEquals(jp.technology, cp.printTechnologyCode)))
        .map(jp => ({ printPositionCode: jp.position, printTechnologyCode: jp.technology }));

    // TD hard-fails ("fatal error dialog") when a removal would leave the job without any variant or
    // print option. The cart config always contains at least one of each, so a full wipe can only mean
    // the code namespaces did not line up (everything looks added AND removed) — in that degenerate
    // case, keep the additions and skip the removals rather than risk the fatal dialog.
    if (removeVariants.length && removeVariants.length >= jobVariants.length && !addVariants.length) {
        devLog("TronDesigner: skipping variant removals — they would empty the print job", { removeVariants });
        removeVariants = [];
    }
    if (removePrintings.length && removePrintings.length >= jobPrintings.length && !addPrintings.length) {
        devLog("TronDesigner: skipping printing removals — they would empty the print job", { removePrintings });
        removePrintings = [];
    }

    return { addVariants, updateVariants, removeVariants, addPrintings, removePrintings };
}

// Opens a LINKED print job in Edit mode, synchronized with the print set's current TCC config: the
// computed additions/removals are passed as editor arguments so the design reflects what is priced.
// Falls back to a plain Edit open when the live job cannot be read (the sync is an enhancement, not a
// precondition for editing).
async function openDesignerEditSynced(printSetCartItemId: string, context: CartDesignerEditorContext): Promise<void> {
    const printJobGuid = context.printJobGuid;
    if (!printJobGuid) {
        return;
    }

    let printJob: IPrintJobDto | null | undefined = null;
    try {
        const td = await getTronDesignerAsync();
        printJob = await td.getPrintJob(printJobGuid);
    } catch (err) {
        console.warn("TronDesigner: could not read the print job for config sync — opening without it.", { printJobGuid, err });
    }

    if (!printJob) {
        openDesignerLocked(printJobGuid, printSetCartItemId, context.calculationGuid);
        return;
    }

    // The raw job state the delta was computed FROM — a delta that "adds" a variant the cart never
    // changed means getPrintJob returned the job without its variants (TD data/DTO drift), and this
    // is the only place that evidence is visible.
    devLog("TronDesigner: config sync source print job", {
        variants: (printJob.product?.variants ?? []).map(v => ({ codeSupplier: v.codeSupplier, codePtn: v.codePtn, quantity: v.quantity })),
        printings: (printJob.printings ?? []).map(p => ({ position: p.printData?.positionCodePtn, technology: p.printData?.technologyCodePtn })),
    });
    const delta = computeEditorConfigSyncDelta(context, printJob);
    devLog("TronDesigner: edit-mode config sync delta", delta);

    waitForTronDesigner(td => {
        const printJobArgs: ArgPrintJobWithRemovals = {
            languageCode: getShopLanguageCode(),
            printJobGuid,
            ...(context.productTitle ? { title: context.productTitle } : {}),
            ...((delta.addVariants.length || delta.updateVariants.length) ? {
                addProducts: {
                    supplierGuid: context.supplierGuid || undefined,
                    // TD routes each entry by codeSupplier: unknown codes are added, existing ones get
                    // their quantity/overrides updated in place (quantity sync, TD PR 4281).
                    variants: [...delta.addVariants, ...delta.updateVariants],
                }
            } : {}),
            ...(delta.addPrintings.length ? { addPrintings: delta.addPrintings } : {}),
            ...(delta.removeVariants.length ? { removeProducts: { variants: delta.removeVariants } } : {}),
            ...(delta.removePrintings.length ? { removePrintings: delta.removePrintings } : {}),
            // Identity on the job; orderItemCode = the live cart print-set id (re-link target), NOT the order
            // code. The calculation guid moves to custom data (§4.3 — order code is reserved for the real order
            // number, which does not exist at design time).
            customer: {
                ...readCustomerIdentity(),
                orderItemCode: printSetCartItemId,
            },
            customProperties: buildCustomProperties({
                [PRINT_SET_CART_ITEM_ID_KEY]: printSetCartItemId,
                calculationGuid: context.calculationGuid,
                productCartItemId: context.productCartItemId,
                productCode: context.shopProductCode,
                modelCode: context.modelCode,
                supplierGuid: context.supplierGuid,
            }),
        };

        // No editorOptions on an Edit open — see openDesignerLocked (the "Selected" filter left the mockup
        // unresolved and crashed the editor). The config-sync add/remove deltas, when present, ride on
        // printJobArgs; they are a WIP TD feature and out of scope here. Cast: editorOptions is type-required
        // but omitted at runtime, matching the reference.
        const args = {
            openMode: "Edit",
            printJob: printJobArgs,
        } as unknown as EditorArguments;

        openDesignerDialog(td, args);
    });
}

// The current TCC print specs of the print set as editor add-arguments (shared by the create path and
// the edit-mode config sync).
function buildContextAddPrintings(context: CartDesignerEditorContext): ArgAddPrinting[] {
    return (context.printData?.printData ?? []).map(item => ({
        printPositionCode: item.printPositionCode,
        printTechnologyCode: item.printTechnologyCode,
        maxColors: item.maxColors,
        maxWidth: item.maxWidth,
        maxHeight: item.maxHeight,
        maxDiameter: item.maxDiameter,
        // Not in the published 1.5.0 types yet — TD editors without PR 4301 ignore the extra field.
        maxArea: item.maxArea,
    } as ArgAddPrinting));
}

// Called with `fallback` when the caller already knows the print job (the cart/order "Edit design"
// button): if the editor context cannot be loaded (e.g. the item is not in the active cart), the job
// still opens in plain Edit mode instead of failing silently.
export async function openDesignerForCartCalculation(
    printSetCartItemId: string,
    fallback?: { printJobGuid?: string; calculationId?: string }
): Promise<void> {
    devLog("TronDesigner: loading editor context", { printSetCartItemId });

    const response = await new HttpClient().fetch(
        `/api/cart/${encodeURIComponent(printSetCartItemId)}/designer-editor-context`,
        undefined,
        () => { /* handled below — a missing context falls back to a plain Edit open when possible */ }
    );

    if (!response?.ok) {
        devLog("TronDesigner: editor context request failed", { status: response?.status, printSetCartItemId });
        if (fallback?.printJobGuid) {
            openDesignerLocked(fallback.printJobGuid, printSetCartItemId, fallback.calculationId);
        } else {
            console.error("TronDesigner: failed to load editor context for cart calculation", response?.status);
        }
        return;
    }

    const context = await response.json() as CartDesignerEditorContext;
    devLog("TronDesigner: editor context loaded", context);

    if (context.printJobGuid) {
        devLog("TronDesigner: opening existing print job with config sync", { printJobGuid: context.printJobGuid });
        await openDesignerEditSynced(printSetCartItemId, context);
        return;
    }

    const addPrintings = buildContextAddPrintings(context);

    // Exact mode takes the explicit variant list (productCode + quantity); when the cart items carry no
    // supplier product codes, fall back to the primary product's code so the editor still gets one variant.
    const variantItems = (context.variants ?? [])
        .filter(v => !!v.productCode)
        .map(v => ({ productCode: v.productCode, quantity: v.quantity }));

    if (!variantItems.length && context.supplierCode) {
        variantItems.push({ productCode: context.supplierCode, quantity: undefined });
    }

    // Exact mode has no model concept — the variants ARE the product — so opening it with zero variants
    // throws in the editor. A priced calculation always resolves at least one product, so this should
    // never happen; guard defensively by degrading to a Partial open on the supplier model (the customer
    // then picks variants) instead of erroring the editor.
    const useExact = variantItems.length > 0;
    if (!useExact) {
        console.error("TronDesigner: cart calculation resolved no product variants — opening the supplier model in Partial mode.", { printSetCartItemId });
    }

    waitForTronDesigner(async td => {
        devLog("TronDesigner: opening create dialog for cart calculation");
        // §2.1: a logo picked in the cart's TD logo gallery is "taken over by TD Editor" — as a native TD
        // logo it wins over any TL handover candidate (§5.2/§6.1), which applies only when nothing was
        // picked in the cart.
        const logo = context.tronDesignerLogoGuid
            ? { guid: context.tronDesignerLogoGuid }
            : await resolveTronLogoEditorArg();
        // The cart calculation has already fixed the configuration (variants, quantities, print
        // positions/technologies via TCC), so open in Exact dataMode: the editor uses exactly the supplied
        // products and printings instead of loading the supplier model for the customer to pick from
        // (see trondesigner-examples json_exect_basic/builder_exect_basic). Exact mode has no model concept —
        // the variants below ARE the product — and the supplier is identified by GUID (context.supplierCode
        // holds the supplier's PRODUCT code, not a vendor code, so it must not go into addProducts.supplierCode).
        // §4.1 — the shop-facing code + product title shown on the product page, applied to each variant so the
        // proofreading matches the page (never the supplier code, §4.2).
        const overrides = buildProductOverrides(context.shopProductCode, context.productTitle);
        const args: EditorArguments = {
            openMode: "Create",
            dataMode: useExact ? "Exact" : "Partial",
            editorOptions: {
                showSelectedVariants: useExact ? "None" : "All",
                showSelectedPrintOptions: useExact ? "None" : "All"
            },
            printJob: {
                languageCode: getShopLanguageCode(),
                ...(context.productTitle ? { title: context.productTitle } : {}),
                addProducts: useExact
                    ? {
                        supplierGuid: context.supplierGuid || undefined,
                        variants: variantItems.map(v => ({ ...v, ...(overrides ? { overrides } : {}) })),
                    }
                    : {
                        supplierGuid: context.supplierGuid || undefined,
                        ...(context.modelCode ? { model: { modelCode: context.modelCode, ...(context.productTitle ? { title: context.productTitle } : {}) } } : {}),
                        // Partial mode loads the supplier model for the customer to pick variants from;
                        // ArgAddProduct still requires the field, so hand it an empty selection.
                        variants: [],
                    },
                addPrintings: addPrintings.length ? addPrintings : undefined,
                ...(logo ? { logo } : {}),
                // Identity on the job; orderItemCode = the cart print-set id (re-link target), NOT the order code
                // (§4.3 — order code is reserved for the real order number; the calculation guid goes to custom data).
                customer: {
                    ...readCustomerIdentity(),
                    orderItemCode: printSetCartItemId,
                },
                customProperties: buildCustomProperties({
                    [PRINT_SET_CART_ITEM_ID_KEY]: printSetCartItemId,
                    calculationGuid: context.calculationGuid,
                    productCartItemId: context.productCartItemId,
                    productCode: context.shopProductCode,
                    modelCode: context.modelCode,
                    supplierGuid: context.supplierGuid,
                }),
            }
        };

        openDesignerDialog(td, args);
    });
}

export function initTronDesignerCartActions(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-td-cart-action]').forEach(btn => {
        if (btn.dataset.tdCartActionBound) return;
        btn.dataset.tdCartActionBound = "1";

        btn.addEventListener('click', () => {
            const action = btn.dataset.tdCartAction as "create" | "edit";
            const cartItemId = btn.dataset.cartItemId;
            const calculationId = btn.dataset.calculationId;
            const printJobGuid = btn.dataset.tdPrintJobGuid;

            if (action === "edit" && printJobGuid && cartItemId) {
                // Context-aware edit: loads the current TCC config and syncs the print job with it
                // (added/removed variants and print options, §4.4). Falls back to a plain Edit open
                // when the item has no cart context (e.g. the order-detail page).
                void openDesignerForCartCalculation(cartItemId, { printJobGuid, calculationId });
            } else if (action === "edit" && printJobGuid) {
                openDesignerLocked(printJobGuid, cartItemId, calculationId);
            } else if (cartItemId) {
                void openDesignerForCartCalculation(cartItemId);
            } else {
                waitForTronDesigner(async td => {
                    // No product context on the cart-action button; TronDesigner resolves the product from
                    // the order item code. openDesigner (not the builder's .open()) is the reliable entry.
                    // TODO: if the editor needs explicit product data for cart "create", carry supplier/model
                    // codes on the [data-td-cart-action] element and thread them through here.
                    const logo = await resolveTronLogoEditorArg();
                    const args: EditorArguments = {
                        openMode: "Create",
                        dataMode: "Partial",
                        editorOptions: {
                            showSelectedVariants: "All",
                            showSelectedPrintOptions: "All"
                        },
                        printJob: {
                            languageCode: getShopLanguageCode(),
                            // Pre-load the customer's TronLogo when the §5.2/§6.1 handover rules allow it.
                            ...(logo ? { logo } : {}),
                            // Identity on the job; orderItemCode = the cart item (re-link target), NOT the order
                            // code. The calculation guid goes to custom data (§4.3 — no order exists yet here).
                            customer: {
                                ...readCustomerIdentity(),
                                ...(cartItemId ? { orderItemCode: cartItemId } : {})
                            },
                            customProperties: buildCustomProperties({
                                [PRINT_SET_CART_ITEM_ID_KEY]: cartItemId,
                                calculationGuid: calculationId,
                            })
                        }
                    };
                    openDesignerDialog(td, args);
                });
            }
        });
    });

    document.querySelectorAll<HTMLElement>('[data-td-download-proofreading]').forEach(link => {
        if (link.dataset.tdDownloadProofreadingBound) return;
        link.dataset.tdDownloadProofreadingBound = "1";

        link.addEventListener('click', evt => {
            evt.preventDefault();

            const printJobGuid = link.dataset.tdPrintJobGuid;
            if (!printJobGuid) {
                return;
            }

            // Scope the loading overlay to the print (Product Printing) group, falling back to the button.
            const overlayTarget = link.closest<HTMLElement>('.cart-preview-printing-set') ?? link;
            void onDownloadPdf(printJobGuid, overlayTarget);
        });
    });
}

